import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { loginUser } from '../lib/api';
import { setLoginEmailForSession } from '../lib/adminEmail';
import './login.css';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fbStatus, setFbStatus] = useState('');

  const appId = '981071426908731';
  const configId = '1564406280945997';

  useEffect(() => {
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: appId,
        cookie: true,
        xfbml: true,
        version: 'v20.0',
      });
    };

    (function (d, s, id) {
      let js;
      const fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s);
      js.id = id;
      js.src = 'https://connect.facebook.net/en_US/sdk.js';
      fjs.parentNode.insertBefore(js, fjs);
    })(document, 'script', 'facebook-jssdk');
  }, [appId]);

  const handleFacebookLogin = () => {
    setFbStatus('');
    if (!window.FB) {
      setFbStatus('Facebook SDK not loaded yet. Please try again.');
      return;
    }

    window.FB.login(
      function (response) {
        if (response.status === 'connected') {
          const authCode = response.authResponse?.code;
          if (authCode) {
            setFbStatus('Processing Facebook login...');
            fetch(`${process.env.REACT_APP_API_BASE_URL}/embedded/exchange-code`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: authCode }),
            })
              .then((res) => {
                if (res.status === 200) return res.json();
                throw new Error('Failed to process login.');
              })
              .then((data) => {
                const verified_name = data.verified_name;
                if (verified_name) {
                  setFbStatus(
                    `Welcome, ${verified_name}! Registration successful. Verification in progress.`
                  );
                } else {
                  setFbStatus('No verified name found in the response.');
                }
              })
              .catch((err) => {
                setFbStatus(
                  err?.message || 'An error occurred during Facebook login.'
                );
              });
          } else {
            setFbStatus('Facebook login was not successful. Please try again.');
          }
        } else {
          setFbStatus('Facebook login was not successful. Please try again.');
        }
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
      }
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await loginUser({ email, password });
      const token = data.token;
      jwtDecode(token); // decoded available if needed
      setLoginEmailForSession(email);
      navigate('/main');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login1">
      <div className="login-shell">
        <div className="login-brand-wrapper">
          <div className="login-brand">IISSMS</div>
          <div className="login-brand-subtitle">Business Messaging Solution</div>
        </div>
        <div className="login-container">
          <div className="login-left">
            <div className="login-header">
              <div className="subtitle">Sign in to your account</div>
            </div>
            <p className="login-description">
              Welcome to IISSMS. Manage your business messaging campaigns and templates.
            </p>
            <form onSubmit={onSubmit} className="login-form">
            <div className="field">
              <label className="label" htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                className="input"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
              <div className="field">
                <label className="label" htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  className="input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error && <div className="error">{error}</div>}
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
            <div className="hr-or"><span>or</span></div>
            <button type="button" className="btn btn-fb" onClick={handleFacebookLogin}>
              Login with Facebook
            </button>
            {fbStatus && <div className="fb-status">{fbStatus}</div>}
            <p className="login-terms">
              By signing in, you agree to our <a href="#tos">Terms of Service</a> and <a href="#privacy">Privacy Policy</a>.
            </p>
            <p className="login-support">
              Need help? <a href="#support">Contact Support</a>
            </p>
          </div>
          <div className="login-right">
            <div className="login-illustration">
            <svg
              viewBox="0 0 200 200"
              xmlns="http://www.w3.org/2000/svg"
              className="login-svg"
              aria-hidden
            >
              <defs>
                <linearGradient id="loginBg" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#e5f9f3" />
                  <stop offset="100%" stopColor="#f5fdfb" />
                </linearGradient>
                <linearGradient id="loginAccent" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#00a884" />
                  <stop offset="100%" stopColor="#25d366" />
                </linearGradient>
                <linearGradient id="loginBubbleGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="100%" stopColor="#e7f7ef" />
                </linearGradient>
              </defs>
              {/* Background planet */}
              <g className="login-planet">
                <circle cx="100" cy="100" r="86" fill="url(#loginBg)" />
              </g>

              {/* App window */}
              <g className="login-window">
                <rect
                  x="34"
                  y="34"
                  rx="20"
                  ry="20"
                  width="132"
                  height="120"
                  fill="#ffffff"
                  stroke="rgba(148, 163, 184, 0.6)"
                  strokeWidth="1"
                />
                <rect
                  x="46"
                  y="44"
                  rx="6"
                  ry="6"
                  width="58"
                  height="10"
                  fill="rgba(148, 163, 184, 0.18)"
                />
                <circle cx="140" cy="49" r="2.3" fill="#22c55e" />
                <circle cx="148" cy="49" r="2.3" fill="#f97316" />
              </g>

              {/* Incoming message bubble */}
              <g className="login-bubble-left">
                <path
                  d="M56 80h50c4 0 7 3 7 7v12c0 4-3 7-7 7H70l-8 6-1-6c-3 0-5-3-5-7V87c0-4 3-7 7-7z"
                  fill="url(#loginBubbleGrad)"
                  stroke="rgba(34,197,94,0.4)"
                  strokeWidth="1"
                />
                <rect x="64" y="85" width="35" height="4" rx="2" fill="#166534" />
                <rect x="64" y="92" width="26" height="3" rx="1.5" fill="#16a34a" opacity="0.7" />
              </g>

              {/* Outgoing message bubble */}
              <g className="login-bubble-right">
                <path
                  d="M94 112h44c4 0 7 3 7 7v12c0 4-3 7-7 7h-20l-6 5-1-5c-3 0-5-3-5-7v-12c0-4 3-7 7-7z"
                  fill="#e0f2fe"
                  stroke="rgba(37,99,235,0.35)"
                  strokeWidth="1"
                />
                <rect x="102" y="117" width="30" height="4" rx="2" fill="#1d4ed8" />
                <rect x="102" y="124" width="24" height="3" rx="1.5" fill="#60a5fa" opacity="0.8" />
              </g>

              {/* Typing indicator */}
              <g className="login-typing">
                <rect
                  x="70"
                  y="132"
                  rx="8"
                  ry="8"
                  width="36"
                  height="18"
                  fill="#0f172a"
                  opacity="0.9"
                />
                <circle cx="78" cy="141" r="2" className="login-typing-dot" />
                <circle cx="86" cy="141" r="2" className="login-typing-dot" />
                <circle cx="94" cy="141" r="2" className="login-typing-dot" />
              </g>

              {/* Accent ring */}
              <g className="login-ring">
                <circle
                  cx="100"
                  cy="100"
                  r="88"
                  fill="none"
                  stroke="url(#loginAccent)"
                  strokeWidth="1"
                  strokeDasharray="4 8"
                  opacity="0.5"
                />
              </g>
            </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
